import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Alert, Spinner, Form, Badge, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTenants, fetchTenantQuestions, saveTenantQuestions, reloadConfig, fetchTenantConfigFromDB } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

const TABS = [
    { id: 'weights', label: 'Weights'          },
    { id: 'bands',   label: 'SRRI Score Bands' },
    { id: 'mapping', label: 'Portfolio Mapping' },
];

function SumIndicator({ weights, label }) {
    const total = weights.reduce((s, w) => s + w, 0);
    const ok = Math.abs(total - 1.0) < 0.001;
    return (
        <div className={`py-2 small ${ok ? 'text-success' : 'text-danger fw-semibold'}`}>
            {label} total: {(total * 100).toFixed(2)}%{ok ? ' ✓' : ' — must equal 100%'}
        </div>
    );
}

function WeightRow({ id, text, weight, onChange }) {
    return (
        <div className="d-flex justify-content-between align-items-center py-2 px-1"
            style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ flex: 1, paddingRight: 16 }}>
                <span className="fw-semibold me-2"
                    style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{id}</span>
                <span style={{ fontSize: 13 }}>{text}</span>
            </div>
            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                <Form.Control
                    type="number" size="sm" step={0.1} min={0} max={100}
                    value={parseFloat((weight * 100).toFixed(4))}
                    onChange={e => onChange(parseFloat(e.target.value) / 100 || 0)}
                    style={{ width: 90, textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }}
                />
                <span style={{ fontSize: 12, color: '#64748b' }}>%</span>
            </div>
        </div>
    );
}

function Scoring({ tenantCode }) {
    const { can } = useAuth();
    const [data, setData] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [selectedTenant, setSelectedTenant] = useState(tenantCode || '');
    const [activeTab, setActiveTab] = useState('weights');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [isDefault, setIsDefault] = useState(false);   // tenant has no row → showing shared template
    const [portfolioLevels, setPortfolioLevels] = useState({});   // the tenant's configured levels (Configuration → Portfolio Levels)

    // Embedded in the Tenants detail view the tenant is fixed via the prop (no picker); standalone,
    // load the tenant list and default to the first (methodology is per-tenant).
    useEffect(() => {
        if (tenantCode) { setSelectedTenant(tenantCode); return; }
        fetchTenants()
            .then(d => {
                const list = d.tenants || [];
                setTenants(list);
                if (list.length) setSelectedTenant(list[0].code);
                else setLoading(false);
            })
            .catch(() => { setFeedback({ type: 'danger', msg: 'Failed to load tenants.' }); setLoading(false); });
    }, [tenantCode]);

    const load = useCallback(() => {
        if (!selectedTenant) return;
        setLoading(true);
        Promise.all([fetchTenantQuestions(selectedTenant), fetchTenantConfigFromDB(selectedTenant)])
            .then(([q, cfg]) => {
                setData(q.questions || {});
                setIsDefault(q.is_default || false);
                setPortfolioLevels(cfg.portfolio_levels || {});
                setDirty(false);
            })
            .catch(() => setFeedback({ type: 'danger', msg: 'Failed to load scoring methodology.' }))
            .finally(() => setLoading(false));
    }, [selectedTenant]);

    useEffect(() => { load(); }, [load]);

    const mark = () => { setDirty(true); setFeedback(null); };

    const setRtWeight = (v) => {
        setData(prev => ({
            ...prev,
            methodology: { ...prev.methodology, tolerance_weight: v },
            tolerance: { ...prev.tolerance, overall_weight: v },
        }));
        mark();
    };

    const setCflWeight = (v) => {
        setData(prev => ({
            ...prev,
            methodology: { ...prev.methodology, capacity_weight: v },
            capacity: { ...prev.capacity, overall_weight: v },
        }));
        mark();
    };

    const setRtQuestionWeight = (idx, v) => {
        setData(prev => {
            const questions = [...prev.tolerance.questions];
            questions[idx] = { ...questions[idx], weight: v };
            return { ...prev, tolerance: { ...prev.tolerance, questions } };
        });
        mark();
    };

    const setCflQuestionWeight = (idx, v) => {
        setData(prev => {
            const questions = [...prev.capacity.questions];
            questions[idx] = { ...questions[idx], weight: v };
            return { ...prev, capacity: { ...prev.capacity, questions } };
        });
        mark();
    };

    const setBandMin = (idx, v) => {
        setData(prev => {
            const srri_bands = [...prev.methodology.srri_bands];
            srri_bands[idx] = { ...srri_bands[idx], min: parseFloat(v) || 0 };
            return { ...prev, methodology: { ...prev.methodology, srri_bands } };
        });
        mark();
    };

    const setBandMax = (idx, v) => {
        setData(prev => {
            const srri_bands = [...prev.methodology.srri_bands];
            srri_bands[idx] = { ...srri_bands[idx], max: parseFloat(v) || 0 };
            return { ...prev, methodology: { ...prev.methodology, srri_bands } };
        });
        mark();
    };

    const setPortfolioLevel = (srri, level) => {
        if (level === '' || level == null) return;
        setData(prev => ({
            ...prev,
            methodology: {
                ...prev.methodology,
                srri_to_portfolio_level: {
                    ...prev.methodology.srri_to_portfolio_level,
                    mapping: {
                        ...prev.methodology.srri_to_portfolio_level.mapping,
                        [String(srri)]: parseInt(level),
                    },
                },
            },
        }));
        mark();
    };

    const validate = () => {
        if (!data?.tolerance?.questions || !data?.capacity?.questions) return 'Questionnaire is incomplete for this tenant.';
        const rtTotal  = data.tolerance.questions.reduce((s, q) => s + q.weight, 0);
        const cflTotal = data.capacity.questions.reduce((s, q) => s + q.weight, 0);
        const overall  = data.tolerance.overall_weight + data.capacity.overall_weight;
        if (Math.abs(overall  - 1.0) >= 0.001) return 'RT + CFL overall weights must sum to 100%.';
        if (Math.abs(rtTotal  - 1.0) >= 0.001) return 'RT question weights must sum to 100%.';
        if (Math.abs(cflTotal - 1.0) >= 0.001) return 'CFL question weights must sum to 100%.';
        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) { setFeedback({ type: 'danger', msg: err }); return; }
        setSaving(true);
        setFeedback(null);
        try {
            await saveTenantQuestions(selectedTenant, data);
            setDirty(false);
            setIsDefault(false);
            setFeedback({ type: 'success', msg: `Scoring methodology saved for ${selectedTenant}.` });
        } catch (e) {
            const detail = e.response?.data?.detail;
            setFeedback({ type: 'danger', msg: Array.isArray(detail) ? detail[0]?.msg : (detail || 'Save failed.') });
        } finally {
            setSaving(false);
        }
    };

    const handleReload = async () => {
        try {
            await reloadConfig();
            load();
            setFeedback({ type: 'info', msg: 'Configuration reloaded from disk.' });
        } catch {
            setFeedback({ type: 'danger', msg: 'Reload failed.' });
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            </div>
        );
    }

    const spl = data?.methodology?.srri_to_portfolio_level || {};
    const mapping = spl.mapping || {};

    // Only the portfolio levels actually configured for this tenant (Configuration → Portfolio Levels)
    // may be mapped to — an SRRI must not point at a level with no allocation row.
    const levelOptions = Object.keys(portfolioLevels).map(Number).sort((a, b) => a - b);

    // Derive SRRI rows from mapping keys
    const srriKeys = Object.keys(mapping).map(Number).sort((a, b) => a - b);

    return (
        <div className="page-content">
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        Scoring Methodology
                        {dirty && (
                            <Badge bg="warning" text="dark" className="ms-2"
                                style={{ fontSize: 11, verticalAlign: 'middle' }}>
                                Unsaved changes
                            </Badge>
                        )}
                    </h1>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        Per-tenant SRRI calculation weights, normalised score bands, and portfolio level mapping.
                    </p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    {!tenantCode && (
                        <Form.Select
                            size="sm" value={selectedTenant} disabled={saving || !tenants.length}
                            onChange={e => setSelectedTenant(e.target.value)}
                            style={{ width: 200, fontSize: 13 }}>
                            {tenants.map(t => (
                                <option key={t.code} value={t.code}>{t.name || t.code}</option>
                            ))}
                        </Form.Select>
                    )}
                    <Button variant="outline-secondary" size="sm" onClick={handleReload} disabled={saving || !can.editAdviceConfig()}
                        title={can.editAdviceConfig() ? undefined : "Requires the waive-config role"}>
                        <FontAwesomeIcon icon="rotate-right" className="me-1" />
                        Reload
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || (!dirty && !isDefault) || !can.editAdviceConfig()}
                        title={can.editAdviceConfig() ? undefined : "Requires the waive-config role"}>
                        {saving && <Spinner size="sm" animation="border" className="me-1" />}
                        {isDefault ? 'Create for this tenant' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert variant={feedback.type} className="mb-3 py-2" style={{ fontSize: 13 }}
                    dismissible onClose={() => setFeedback(null)}>
                    {feedback.msg}
                </Alert>
            )}

            {isDefault && (
                <Alert variant="info" className="mb-3 py-2" style={{ fontSize: 13 }}>
                    This tenant has no scoring methodology of its own yet — you&apos;re viewing the shared
                    {' '}<strong>default template</strong>. Adjust the weights/bands and save to create a
                    tenant-specific copy.
                </Alert>
            )}

            <div className="d-flex gap-1 mb-3" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                        background: activeTab === t.id ? '#f1f5f9' : 'transparent',
                        border: 'none', borderRadius: 6, padding: '7px 14px',
                        fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400,
                        color: activeTab === t.id ? '#0f172a' : '#64748b', cursor: 'pointer',
                    }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {data && Object.keys(data).length === 0 && (
                <Alert variant="light" className="border" style={{ fontSize: 13 }}>
                    This tenant has no questionnaire / scoring methodology yet.
                </Alert>
            )}

            {activeTab === 'weights' && data?.tolerance?.questions && data?.capacity?.questions && (
                <Row className="g-3">
                    <Col xs={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-transparent border-bottom py-2 px-4">
                                <span className="fw-semibold" style={{ fontSize: 13 }}>
                                    Overall Split — Risk Tolerance vs Capacity for Loss
                                </span>
                            </Card.Header>
                            <Card.Body style={{ padding: '0 24px' }}>
                                <WeightRow id="RT"  text="Risk Tolerance (RT)"
                                    weight={data.tolerance.overall_weight} onChange={setRtWeight} />
                                <WeightRow id="CFL" text="Capacity for Loss (CFL)"
                                    weight={data.capacity.overall_weight}  onChange={setCflWeight} />
                                <SumIndicator
                                    weights={[data.tolerance.overall_weight, data.capacity.overall_weight]}
                                    label="RT + CFL" />
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Header className="bg-transparent border-bottom py-2 px-4">
                                <span className="fw-semibold" style={{ fontSize: 13 }}>
                                    Risk Tolerance — Question Weights
                                </span>
                            </Card.Header>
                            <Card.Body style={{ padding: '0 24px' }}>
                                {data.tolerance.questions.map((q, i) => (
                                    <WeightRow key={q.id} id={q.id} text={q.text}
                                        weight={q.weight} onChange={v => setRtQuestionWeight(i, v)} />
                                ))}
                                <SumIndicator
                                    weights={data.tolerance.questions.map(q => q.weight)}
                                    label="RT questions" />
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Header className="bg-transparent border-bottom py-2 px-4">
                                <span className="fw-semibold" style={{ fontSize: 13 }}>
                                    Capacity for Loss — Question Weights
                                </span>
                            </Card.Header>
                            <Card.Body style={{ padding: '0 24px' }}>
                                {data.capacity.questions.map((q, i) => (
                                    <WeightRow key={q.id} id={q.id} text={q.text}
                                        weight={q.weight} onChange={v => setCflQuestionWeight(i, v)} />
                                ))}
                                <SumIndicator
                                    weights={data.capacity.questions.map(q => q.weight)}
                                    label="CFL questions" />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'bands' && data?.methodology?.srri_bands && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-transparent border-bottom py-2 px-4 d-flex align-items-center gap-3">
                        <span className="fw-semibold" style={{ fontSize: 13 }}>SRRI Score Bands</span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Normalised score (0–1) where score = (weighted_avg − 1) / 3. Bands may overlap at boundaries.
                        </span>
                    </Card.Header>
                    <Card.Body style={{ padding: '0 24px' }}>
                        <div className="d-flex py-2 px-1"
                            style={{ borderBottom: '2px solid #e2e8f0', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                            <div style={{ width: 80 }}>SRRI</div>
                            <div style={{ width: 160 }}>Min score</div>
                            <div style={{ width: 160 }}>Max score</div>
                        </div>
                        {data.methodology.srri_bands.map((band, i) => (
                            <div key={band.srri}
                                className="d-flex align-items-center py-2 px-1"
                                style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <div style={{ width: 80 }}>
                                    <Badge bg="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                        SRRI {band.srri}
                                    </Badge>
                                </div>
                                <div style={{ width: 160 }}>
                                    <Form.Control
                                        type="number" size="sm" step={0.01} min={0} max={1}
                                        value={band.min}
                                        onChange={e => setBandMin(i, e.target.value)}
                                        style={{ width: 110, textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }}
                                    />
                                </div>
                                <div style={{ width: 160 }}>
                                    <Form.Control
                                        type="number" size="sm" step={0.01} min={0} max={1}
                                        value={band.max}
                                        onChange={e => setBandMax(i, e.target.value)}
                                        style={{ width: 110, textAlign: 'right', fontFamily: 'monospace', fontSize: 13 }}
                                    />
                                </div>
                            </div>
                        ))}
                    </Card.Body>
                </Card>
            )}

            {activeTab === 'mapping' && data?.methodology && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-transparent border-bottom py-2 px-4 d-flex align-items-center gap-3">
                        <span className="fw-semibold" style={{ fontSize: 13 }}>
                            SRRI → Portfolio Level Mapping
                        </span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Maps SRRI (1–7) to a portfolio level configured for this tenant. Names come from
                            Configuration → Portfolio Levels.
                        </span>
                    </Card.Header>
                    <Card.Body style={{ padding: '0 24px' }}>
                        {levelOptions.length === 0 ? (
                            <div className="py-3 text-muted" style={{ fontSize: 13 }}>
                                No portfolio levels configured for this tenant yet — add them in
                                {' '}<strong>Configuration → Portfolio Levels</strong> before mapping SRRIs.
                            </div>
                        ) : (
                            <>
                                <div className="d-flex py-2 px-1"
                                    style={{ borderBottom: '2px solid #e2e8f0', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                                    <div style={{ width: 80 }}>SRRI</div>
                                    <div style={{ width: 100 }}>Level</div>
                                    <div style={{ flex: 1 }}>Portfolio name</div>
                                </div>
                                {srriKeys.map(srri => {
                                    const level = mapping[String(srri)];
                                    const configured = level != null && levelOptions.includes(Number(level));
                                    const name = portfolioLevels[String(level)]?.name;
                                    return (
                                        <div key={srri}
                                            className="d-flex align-items-center py-2 px-1"
                                            style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <div style={{ width: 80 }}>
                                                <Badge bg="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                    SRRI {srri}
                                                </Badge>
                                            </div>
                                            <div style={{ width: 100 }}>
                                                <Form.Select
                                                    size="sm"
                                                    value={configured ? String(level) : ''}
                                                    isInvalid={!configured}
                                                    onChange={e => setPortfolioLevel(srri, e.target.value)}
                                                    style={{ width: 76, fontFamily: 'monospace', fontSize: 13 }}
                                                >
                                                    <option value="" disabled>—</option>
                                                    {levelOptions.map(l => (
                                                        <option key={l} value={String(l)}>{l}</option>
                                                    ))}
                                                </Form.Select>
                                            </div>
                                            <div style={{ flex: 1, fontSize: 13 }}>
                                                {configured
                                                    ? (name || <span className="text-muted fst-italic">(unnamed — set a name in Configuration → Portfolio Levels)</span>)
                                                    : <span className="text-danger">Level {String(level)} is not configured for this tenant</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

export default Scoring;
